import React, { useContext, useEffect, useRef, useState } from 'react';
import ConfigContext from '../../contexts/ConfigContext';
import { useAddMailingListSignupMutation } from '../../redux/slices/mailingListSlice';

// Components
import Modal from './Modal';

import styles from './Footer.module.postcss';

// The line under the field when a sign-up is refused. The server's own
// sentence when the answer is one of the sign-up's (those carry a code);
// the rate limiter's 429 and anything unexpected get a line from here.
export function mailingListError(err) {
  if (err && err.data && err.data.code && err.data.error) return err.data.error;
  if (err && err.status === 429) return 'Too many tries. Try again later.';
  return 'We couldn’t sign you up. Try again.';
}

// The footer's "Join our mailing list" box (task #330). The site keeps the
// list itself: the address is saved unconfirmed and emailed one link, and it
// is on the list only once that link's button is pressed. So the pop-up says
// to check for the email; it never says "subscribed".
function FooterMailingList() {
  const { site_title, street_address, city_state_zip } = useContext(ConfigContext);

  const emailInput = useRef(null);
  const submitButton = useRef(null);
  // { email, already } once the server has answered: `already` when a link
  // went to that address in the last 8 hours and no new one was sent.
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);
  const [addMailingListSignup, { isLoading }] = useAddMailingListSignupMutation();

  // When the pop-up closes, the keyboard goes to the Subscribe button. The
  // pop-up hands it back to whatever had it before, but after a click on
  // Subscribe that is nothing (a button disabled mid-request cannot keep
  // focus) and the page would start again from the top. The button, not the
  // field: focusing a field brings up a phone's keyboard.
  const popUpWasOpen = useRef(false);
  useEffect(() => {
    if (popUpWasOpen.current && !sent && submitButton.current) submitButton.current.focus();
    popUpWasOpen.current = Boolean(sent);
  }, [sent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = emailInput.current.value.trim();
    setError(null);
    try {
      const answer = await addMailingListSignup(email).unwrap();
      setSent({ email, already: answer.status === 'already_sent' });
    } catch (err) {
      setError(mailingListError(err));
      // Back to the field, where the address is fixed, but only when the
      // keyboard is nowhere: after a click on Subscribe it is, for the same
      // reason as above. A visitor who moved on to something else while a
      // slow answer was on its way keeps their place.
      const active = document.activeElement;
      if (emailInput.current && (!active || active === document.body)) emailInput.current.focus();
    }
  };

  return (
    <>
      <label htmlFor='mailing-list-email' className={styles.Footer_heading}>Join our mailing list</label>
      <form className={styles.InlineForm} onSubmit={handleSubmit}>
        <input
          type='email'
          id='mailing-list-email'
          ref={emailInput}
          placeholder='Email address'
          autoComplete='email'
          required
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? 'mailing-list-error' : undefined}
        />
        <button className='Button' type='submit' ref={submitButton} disabled={isLoading}>
          Subscribe
        </button>
      </form>
      { error &&
        <p id='mailing-list-error' role='alert' className={styles.Footer_formError}>{error}</p>
      }

      { sent &&
        <Modal ariaLabel='Check your email'>
          <h4>Check your email.</h4>
          { sent.already
            ? <p>We already sent a link to {sent.email}. Look in your inbox and spam folder, or try again tomorrow.</p>
            : <p>We sent a link to {sent.email}. Open it to join the mailing list.</p>
          }
          <p>
            {site_title}<br/>
            {street_address}<br/>
            {city_state_zip}
          </p>
          <button className='Button' type='button' data-dismiss onClick={() => setSent(null)}>
            Close
          </button>
        </Modal>
      }
    </>
  );
}

export default FooterMailingList;
